/**
 * API Service - Frontend interface to backend REST API
 * 
 * Handles all HTTP requests to the Express/SQLite backend
 * Base URL configurable via environment variable
 */

import { DeviceTemplate, Rig } from '../types';

// Use relative URL in production (same server), localhost:3001 in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api');

// ============================================================================
// Error Handling
// ============================================================================

class APIError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'APIError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new APIError(response.status, error.error || error.message || 'Unknown error');
  }
  return response.json();
}

// ============================================================================
// Device Templates API
// ============================================================================

export const templatesAPI = {
  /**
   * Get all device templates
   */
  async getAll(): Promise<DeviceTemplate[]> {
    const response = await fetch(`${API_BASE_URL}/templates`);
    return handleResponse<DeviceTemplate[]>(response);
  },

  /**
   * Get single template by ID
   */
  async getById(id: string): Promise<DeviceTemplate> {
    const response = await fetch(`${API_BASE_URL}/templates/${id}`);
    return handleResponse<DeviceTemplate>(response);
  },

  /**
   * Create a new custom template
   */
  async create(template: Omit<DeviceTemplate, 'id' | 'createdAt' | 'updatedAt'>): Promise<DeviceTemplate> {
    const now = new Date().toISOString();
    const newTemplate: DeviceTemplate = {
      ...template,
      id: `custom-${Date.now()}`,
      isBuiltIn: false,
      createdAt: now,
      updatedAt: now,
    };

    const response = await fetch(`${API_BASE_URL}/templates`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newTemplate),
    });

    return handleResponse<DeviceTemplate>(response);
  },

  /**
   * Update an existing template (custom only)
   */
  async update(id: string, template: Partial<DeviceTemplate>): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/templates/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...template, updatedAt: new Date().toISOString() }),
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Delete a template (custom only)
   */
  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/templates/${id}`, {
      method: 'DELETE',
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Get templates filtered by type
   */
  async getByType(type: string): Promise<DeviceTemplate[]> {
    const all = await this.getAll();
    return all.filter(t => t.type === type);
  },

  /**
   * Get only built-in templates
   */
  async getBuiltIn(): Promise<DeviceTemplate[]> {
    const all = await this.getAll();
    return all.filter(t => t.isBuiltIn);
  },

  /**
   * Get only custom templates
   */
  async getCustom(): Promise<DeviceTemplate[]> {
    const all = await this.getAll();
    return all.filter(t => !t.isBuiltIn);
  },

  /**
   * Submit a template for admin review
   */
  async submit(templateData: Omit<DeviceTemplate, 'id' | 'createdAt' | 'updatedAt' | 'isBuiltIn'>): Promise<DeviceTemplate> {
    const now = new Date().toISOString();
    const newTemplate = {
      ...templateData,
      id: `template_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      isBuiltIn: false,
      status: 'pending',
      submittedBy: localStorage.getItem('userId') || 'anonymous',
      createdAt: now,
      updatedAt: now,
    };

    const response = await fetch(`${API_BASE_URL}/templates/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newTemplate),
    });

    return handleResponse<DeviceTemplate>(response);
  },
};

// ============================================================================
// Rigs API
// ============================================================================

export const rigsAPI = {
  /**
   * Get all rigs (summary list)
   */
  async getAll(): Promise<Rig[]> {
    const response = await fetch(`${API_BASE_URL}/rigs`);
    return handleResponse<Rig[]>(response);
  },

  /**
   * Get full rig details with instances and connections
   */
  async getById(id: string): Promise<Rig> {
    const response = await fetch(`${API_BASE_URL}/rigs/${id}`);
    return handleResponse<Rig>(response);
  },

  /**
   * Create a new rig
   */
  async create(rig: Omit<Rig, 'id' | 'createdAt' | 'updatedAt'>): Promise<Rig> {
    const now = new Date().toISOString();
    const newRig: Rig = {
      ...rig,
      id: `rig-${Date.now()}`,
      instances: [],
      powerConnections: [],
      usbConnections: [],
      createdAt: now,
      updatedAt: now,
    };

    const response = await fetch(`${API_BASE_URL}/rigs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newRig),
    });

    return handleResponse<Rig>(response);
  },

  /**
   * Delete a rig
   */
  async delete(id: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/rigs/${id}`, {
      method: 'DELETE',
    });

    await handleResponse<{ success: boolean }>(response);
  },
};

// ============================================================================
// Admin API (requires secret key)
// ============================================================================

export const adminAPI = {
  /**
   * Get all pending templates for review
   */
  async getPendingTemplates(adminKey: string): Promise<DeviceTemplate[]> {
    const response = await fetch(`${API_BASE_URL}/admin/templates/pending`, {
      headers: { 'x-admin-key': adminKey },
    });

    return handleResponse<DeviceTemplate[]>(response);
  },

  /**
   * Update a template (before approval)
   */
  async updateTemplate(id: string, template: DeviceTemplate, adminKey: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/templates/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        'x-admin-key': adminKey 
      },
      body: JSON.stringify({
        name: template.name,
        type: template.type,
        brand: template.brand,
        model: template.model,
        description: template.description,
        powerInput: template.powerInput,
        powerOutputs: template.powerOutputs,
        batteryCapacity: template.batteryCapacity,
        usbInputs: template.usbInputs,
        usbOutputs: template.usbOutputs,
      }),
    });

    await handleResponse<{ message: string }>(response);
  },

  /**
   * Approve a pending template
   */
  async approveTemplate(id: string, adminKey: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/templates/${id}/review`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'x-admin-key': adminKey 
      },
      body: JSON.stringify({ action: 'approve' }),
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Reject a pending template
   */
  async rejectTemplate(id: string, adminKey: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/templates/${id}/review`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'x-admin-key': adminKey 
      },
      body: JSON.stringify({ action: 'reject' }),
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Delete a template (admin only)
   */
  async deleteTemplate(id: string, adminKey: string): Promise<void> {
    const response = await fetch(`${API_BASE_URL}/admin/templates/${id}`, {
      method: 'DELETE',
      headers: {
        'x-admin-key': adminKey,
      },
    });
    return handleResponse<void>(response);
  },
};

// ============================================================================
// Health Check
// ============================================================================

export const healthAPI = {
  /**
   * Check if backend is reachable
   */
  async check(): Promise<boolean> {
    try {
      const response = await fetch(`${API_BASE_URL}/templates`, { method: 'HEAD' });
      return response.ok;
    } catch {
      return false;
    }
  },
};

// ============================================================================
// Analytics API
// ============================================================================

export interface AnalyticsStats {
  totalEvents: number;
  uniqueSessions: number;
  rigsCreated: number;
  devicesAdded: number;
  activeSessions24h: number;
  eventsByType: Array<{ event_type: string; count: number }>;
  recentActivity: Array<{ date: string; events: number; sessions: number }>;
}

export const analyticsAPI = {
  /**
   * Get usage analytics stats (admin only)
   */
  async getStats(adminKey: string): Promise<AnalyticsStats> {
    const response = await fetch(`${API_BASE_URL}/analytics/stats?adminKey=${encodeURIComponent(adminKey)}`);
    return handleResponse<AnalyticsStats>(response);
  },
};

// ============================================================================
// Export combined API
// ============================================================================

export const api = {
  templates: templatesAPI,
  rigs: rigsAPI,
  admin: adminAPI,
  health: healthAPI,
  analytics: analyticsAPI,
};

export default api;
